import React, { useState, useEffect } from 'react';
import apiService from '../services/api';
import '../styles/LoginScreen.css';

const LoginScreen = ({ onLogin, onLoginSuccess, isLoading: authLoading, error: authError }) => {
  const [isLoading, setIsLoading] = useState(true);
  const [showLogin, setShowLogin] = useState(false);
  const [mode, setMode] = useState('login'); // 'login' | 'register'
  const [registerSuccess, setRegisterSuccess] = useState(false);
  const [formData, setFormData] = useState({
    email: '',
    password: '',
    confirmPassword: '',
    firstName: '',
    lastName: '',
    practiceName: '',
    rememberMe: false
  });
  const [localError, setLocalError] = useState('');
  const [registerLoading, setRegisterLoading] = useState(false);

  useEffect(() => {
    setIsLoading(false);
    setShowLogin(true);
  }, []);

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
  };

  const switchMode = (newMode) => {
    setMode(newMode);
    setLocalError('');
    setRegisterSuccess(false);
  };

  const handleLoginSubmit = async (e) => {
    e.preventDefault();
    setLocalError('');

    if (!formData.email || !formData.password) {
      setLocalError('Please enter both email and password');
      return;
    }

    try {
      const result = await onLogin({
        email: formData.email,
        password: formData.password,
        rememberMe: formData.rememberMe
      });

      if (result && result.success && onLoginSuccess) {
        onLoginSuccess(result);
      } else if (result && !result.success) {
        let errorMessage = 'Login failed. Please try again.';
        if (result.error) errorMessage = result.error;
        else if (result.errorMessage) errorMessage = result.errorMessage;
        else if (result.detail) errorMessage = result.detail;

        if (errorMessage.toLowerCase().includes('invalid email or password')) {
          errorMessage = 'The email or password you entered is incorrect. Please try again.';
        } else if (errorMessage.toLowerCase().includes('authentication failed')) {
          errorMessage = 'Login failed. Please check your credentials and try again.';
        }
        setLocalError(errorMessage);
      }
    } catch (error) {
      console.error('Login error:', error);
      let errorMessage = 'Unable to connect to the server. Please check your internet connection and try again.';
      if (error.message) {
        if (error.message.includes('NetworkError') || error.message.includes('fetch')) {
          errorMessage = 'Network error. Please check your connection and try again.';
        } else if (error.message.includes('timeout')) {
          errorMessage = 'Request timed out. Please try again.';
        } else if (error.message.includes('401')) {
          errorMessage = 'Invalid credentials. Please check your email and password.';
        }
      }
      setLocalError(errorMessage);
    }
  };

  const handleRegisterSubmit = async (e) => {
    e.preventDefault();
    setLocalError('');

    if (!formData.practiceName || !formData.firstName || !formData.lastName || !formData.email || !formData.password) {
      setLocalError('Please fill in all required fields');
      return;
    }

    if (formData.password !== formData.confirmPassword) {
      setLocalError('Passwords do not match');
      return;
    }

    if (formData.password.length < 8) {
      setLocalError('Password must be at least 8 characters');
      return;
    }

    setRegisterLoading(true);
    try {
      const result = await apiService.register({
        practiceName: formData.practiceName,
        firstName: formData.firstName,
        lastName: formData.lastName,
        email: formData.email,
        password: formData.password,
        confirmPassword: formData.confirmPassword
      });

      if (result && result.success !== false) {
        setRegisterSuccess(true);
        setLocalError('');
      } else {
        setLocalError(result?.errorMessage || result?.detail || 'Registration failed. Please try again.');
      }
    } catch (error) {
      setLocalError('Registration failed. Please check your details and try again.');
    } finally {
      setRegisterLoading(false);
    }
  };

  return (
    <div className="login-screen">
      <div className="login-container">
        {/* MediBooking Logo */}
        <div className="brand-section">
          <div className="medidev-logo">
            <div className="logo-container">
              <img
                src="/PracticeBookrStacked.webp"
                alt="PracticeBookr"
                className="medidev-logo-img"
                onError={(e) => {
                  e.target.style.display = 'none';
                  e.target.nextElementSibling.style.display = 'flex';
                }}
              />
              <div className="logo-text-fallback" style={{ display: 'none' }}>
                <span className="logo-medi">Practice</span>
                <span className="logo-dev">Bookr</span>
              </div>
            </div>
          </div>
          <p className="brand-tagline">Practice Management</p>
          <div className="beta-badge">
            <span className="beta-dot"></span>
            <span>Early Access Beta</span>
          </div>
        </div>

        {/* Right Panel - Forms */}
        <div className="form-panel">
          {isLoading && (
            <div className="loading-section">
              <div className="loading-spinner">
                <div className="spinner-ring"></div>
                <div className="spinner-ring"></div>
                <div className="spinner-ring"></div>
              </div>
              <p className="loading-text">Loading MediBooking...</p>
            </div>
          )}

          {showLogin && mode === 'login' && (
            <div className={`login-section ${showLogin ? 'animate-in' : ''}`}>
              <div className="login-header">
                <h2>Sign In</h2>
              </div>

              <form onSubmit={handleLoginSubmit} className="login-form">
                <div className="form-group">
                  <input
                    type="email"
                    name="email"
                    value={formData.email}
                    onChange={handleInputChange}
                    placeholder="Email address"
                    required
                    disabled={authLoading}
                  />
                </div>

                <div className="form-group">
                  <input
                    type="password"
                    name="password"
                    value={formData.password}
                    onChange={handleInputChange}
                    placeholder="Password"
                    required
                    disabled={authLoading}
                  />
                </div>

                <div className="form-options">
                  <label className="checkbox-label">
                    <input
                      type="checkbox"
                      name="rememberMe"
                      checked={formData.rememberMe}
                      onChange={handleInputChange}
                      disabled={authLoading}
                    />
                    <span className="checkbox-custom"></span>
                    Remember me
                  </label>
                  <span className="forgot-link">Contact your admin to reset your password</span>
                </div>

                {(localError || authError) && (
                  <div className="error-message">{localError || authError}</div>
                )}

                <button
                  type="submit"
                  className={`login-btn ${authLoading ? 'submitting' : ''}`}
                  disabled={authLoading}
                >
                  {authLoading ? (
                    <><div className="btn-spinner"></div>Signing in...</>
                  ) : 'Sign In'}
                </button>
              </form>

            </div>
          )}

          {showLogin && mode === 'register' && (
            <div className={`login-section ${showLogin ? 'animate-in' : ''}`}>
              <div className="login-header">
                <h2>Create Account</h2>
                <p style={{ fontSize: '14px', color: '#666', marginTop: '4px' }}>Set up your practice on MediBooking</p>
              </div>

              {registerSuccess ? (
                <div style={{ textAlign: 'center', padding: '20px 0' }}>
                  <div style={{ fontSize: '48px', marginBottom: '16px' }}>✅</div>
                  <h3 style={{ color: '#1a2942', marginBottom: '8px' }}>Account Created!</h3>
                  <p style={{ color: '#666', marginBottom: '24px' }}>Your practice has been set up successfully.</p>
                  <button
                    className="login-btn"
                    onClick={() => { switchMode('login'); }}
                  >
                    Sign In Now
                  </button>
                </div>
              ) : (
                <form onSubmit={handleRegisterSubmit} className="login-form">
                  <div className="form-group">
                    <input
                      type="text"
                      name="practiceName"
                      value={formData.practiceName}
                      onChange={handleInputChange}
                      placeholder="Practice / clinic name"
                      required
                      disabled={registerLoading}
                    />
                  </div>

                  <div style={{ display: 'flex', gap: '8px' }}>
                    <div className="form-group" style={{ flex: 1 }}>
                      <input
                        type="text"
                        name="firstName"
                        value={formData.firstName}
                        onChange={handleInputChange}
                        placeholder="First name"
                        required
                        disabled={registerLoading}
                      />
                    </div>
                    <div className="form-group" style={{ flex: 1 }}>
                      <input
                        type="text"
                        name="lastName"
                        value={formData.lastName}
                        onChange={handleInputChange}
                        placeholder="Last name"
                        required
                        disabled={registerLoading}
                      />
                    </div>
                  </div>

                  <div className="form-group">
                    <input
                      type="email"
                      name="email"
                      value={formData.email}
                      onChange={handleInputChange}
                      placeholder="Email address"
                      required
                      disabled={registerLoading}
                    />
                  </div>

                  <div className="form-group">
                    <input
                      type="password"
                      name="password"
                      value={formData.password}
                      onChange={handleInputChange}
                      placeholder="Password (min. 8 characters)"
                      required
                      disabled={registerLoading}
                    />
                  </div>

                  <div className="form-group">
                    <input
                      type="password"
                      name="confirmPassword"
                      value={formData.confirmPassword}
                      onChange={handleInputChange}
                      placeholder="Confirm password"
                      required
                      disabled={registerLoading}
                    />
                  </div>

                  {localError && (
                    <div className="error-message">{localError}</div>
                  )}

                  <button
                    type="submit"
                    className={`login-btn ${registerLoading ? 'submitting' : ''}`}
                    disabled={registerLoading}
                  >
                    {registerLoading ? (
                      <><div className="btn-spinner"></div>Creating account...</>
                    ) : 'Create Account'}
                  </button>
                </form>
              )}

              {!registerSuccess && (
                <div style={{ textAlign: 'center', marginTop: '20px', fontSize: '14px', color: '#666' }}>
                  Already have an account?{' '}
                  <button
                    onClick={() => switchMode('login')}
                    style={{ background: 'none', border: 'none', color: '#1a2942', fontWeight: '600', cursor: 'pointer', textDecoration: 'underline' }}
                  >
                    Sign in
                  </button>
                </div>
              )}
            </div>
          )}
        </div>
      </div>

      {/* Background Elements */}
      <div className="login-background">
        <div className="bg-circle bg-circle-1"></div>
        <div className="bg-circle bg-circle-2"></div>
        <div className="bg-circle bg-circle-3"></div>
      </div>
    </div>
  );
};

export default LoginScreen;
